import { useEffect, useMemo, useState } from "react";
import { SerializeFrom } from "@remix-run/node";
import { useFetcher, useMatches } from "@remix-run/react";
import { resetFetcher } from "@core/routes/reset.fetcher.route";

export interface LoaderValue<T> {
  loading: boolean;
  data: SerializeFrom<T> | undefined;
  clear: () => void;
}

const getFullPath = (path: string, params?: Record<string, string>) => {
  if (!params) {
    return path;
  }
  const qp = new URLSearchParams(params);
  return path + "?" + qp.toString();
}

/**
 * Pulls data from a loader at a specified path, manually triggering the loader from the client if it wasn't already
 * loaded.
 * @param path The path to the loader
 * @param params (optional) Query parameters to supply to the loader
 * @returns The data returned by the loader at the given path; if the loader was not executed as part of routing,
 * it will be executed manually via `useFetcher()`. Calls by `useFetcher()` will be automatically deduplicated if
 * multiple components consume the same route.
 * @example
 * // Given the following loader in the file "sample.route.tsx"
 * export async function loader(args) {
 *   const { name } = getQueryParams(args.request);
 *   return json({ greeting: "Hello, " + name });
 * }
 * 
 * // you can consume the loader with the following hook:
 * const { greeting } = useLoader<typeof loader>("/sample", { name: "Sample" });
 * console.log(greeting); // prints "Hello, Sample"
 */
export const useLoader = <T,>(path: string, params?: Record<string, any>): LoaderValue<T> => {
  const [ haveDelayed, setHaveDelayed ] = useState(false);
  const matchingRoutes = useMatches();
  const fetcher = useFetcher<T>({ key: getFullPath(path, params) });
  const route = useMemo(
    () => matchingRoutes.find(r => r.pathname === path),
    [ matchingRoutes, path ]
  );


  useEffect(() => {
    // even with fetcher.load() happening immediately, there is moment
    // when loading is false before the load call even begins.
    // This delay gives time for the fetcher to begin fetching the the data before
    // saying loading is false.
    const doDelay = async () => {
      await new Promise(resolve => {
        const timeout = setTimeout(() => {
          resolve(null);
          clearTimeout(timeout);
        }, 0);
      });
      setHaveDelayed(true);
    };
    doDelay();
  }, []);

  useEffect(() => {
    if (route
      || fetcher.data
      || fetcher.state !== "idle"
    ) {
      return;
    }
    fetcher.load(getFullPath(path, params));
  }, [ route, fetcher, path, params ]);

  const clear = () => resetFetcher(fetcher);

  const data = route?.data as SerializeFrom<T> ?? fetcher.data;
  return { loading: !haveDelayed || fetcher.state !== "idle", data, clear };
};
